import { createRef, PureComponent } from 'react';
import PropTypes from 'prop-types';
import keycode from 'keycode';
import styled, { css } from 'styled-components';
import { Icon } from '@codex/icons';
import { Close } from '@codex/organisms';

const InputWrapper = styled.span`
  position: relative;
  display: inline-block;
  padding: 0;
  width: 100%;
  border: 0;
  border-radius: 4px;
  background-color: #fff;
`;

const Label = styled.label`
  position: absolute;
  top: 14px;
  left: 18px;
  z-index: 0;
  padding: 0;
  width: auto;
  color: #666;
  font-size: 13px;
  transition: all 0.2s ease-in-out;
  pointer-events: none;
  transform: translate3d(0, 0, 0);

  ${({ isOnTop }) =>
    isOnTop &&
    `
      z-index: 1;
      padding: 0 3px;
      transform: translateY(-21px);
      background-color: #fff;
      font-size: 12px;
      transition: all 0.2s ease-in-out;
    `}
`;

const StyledSpan = styled.span`
  position: absolute;
  background-color: transparent;
  padding: 6px 9px;
  cursor: not-allowed;
  width: 38px;
  height: 38px;
  border-radius: 3px;
  top: 3px;
  right: 3px;
  box-sizing: border-box;
  z-index: 1;

  svg {
    color: #4855a0;
    fill: #4855a0;
    stroke: transparent;
    width: 22px;
    height: 22px;
  }
`;

const ClearSpan = styled(StyledSpan)`
  top: 4px;
  right: 42px;
  background-color: #fff;
  cursor: pointer;

  .dc-icon-cross {
    fill: #999 !important;
    stroke: #999 !important;
  }
`;

const SearchEnabledSpan = styled(StyledSpan)`
  background-color: #4855a0;
  z-index: 1;
  cursor: pointer;

  .dc-icon-search > svg {
    fill: #fff;
  }
`;

const ProcessingSpan = styled(StyledSpan)`
  background-color: #f7f7f7;

  .dc-icon {
    width: 22px;
    height: 22px;
    fill: #4855a0;
    stroke: #4855a0;
  }
`;

const Input = styled.input`
  position: relative;
  z-index: 1;
  display: block;
  margin: 0;
  padding: 13px 20px;
  outline: 0;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: transparent !important;
  box-shadow: none;
  color: #333;
  font-size: 13px;
  transition: border 0.2s;
  -webkit-appearance: none;
  box-sizing: border-box;
  width: 100%;
  height: 44px;

  &[type='search'] {
    box-sizing: border-box;
  }

  &:focus ~ .dc-form-item-label {
    outline: 0;
    box-shadow: none;
    z-index: 1;
    padding: 0 3px;
    transform: translateY(-21px);
    background-color: #fff;
    font-size: 12px;
    transition: all 0.2s ease-in-out;
  }

  &::-ms-clear {
    display: none;
  }

  &::-webkit-search-decoration,
  &::-webkit-search-cancel-button,
  &::-webkit-search-results-button,
  &::-webkit-search-results-decoration {
    display: none;
    -webkit-appearance: none;
  }

  ${({ searchInputState }) => {
    if (searchInputState === 'valid') {
      return css`
        &,
        &:focus {
          border-color: #71bd6a;
        }
      `;
    } if (searchInputState === 'invalid') {
      return css`
        &,
        &:focus {
          border-color: #c90404;
        }
      `;
    } if (searchInputState === 'neutral') {
      return css`
        &:focus {
          border-color: #e4c55f;
        }
      `;
    } return null;
  }}

  ${({ searchInputDisabled }) =>
    searchInputDisabled &&
    css`
      &,
      &:focus,
      &:active {
        border-color: #e1e1e1;
        background-color: #f7f7f7 !important;
        color: #a9a9a9;
        opacity: 1;
        -webkit-opacity: 1;
        -webkit-text-fill-color: #a9a9a9;
      }
    `}
`;

export default class LocationSearchInput extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      inputTextValue: ''
    };

    this.onChange = this.onChange.bind(this);
    this.onClearButtonClick = this.onClearButtonClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    this.inputRef = createRef();
    this.searchButtonRef = createRef();
  }

  componentDidUpdate() {
    const { highlightedInputTextSuffix, loading, loadingLabel } = this.props;
    const { inputTextValue } = this.state;

    // when loading, loadingLabel has to be in input
    if (loading) {
      this.inputRef.current.value = loadingLabel;

      return;
    }

    // This fills input value with this.props.highlightedInputTextSuffix value and highlights it
    // if highlightedInputTextSuffix starts with inputTextValue it only highlights the suffix
    if (highlightedInputTextSuffix) {
      this.inputRef.current.value = this.props.highlightedInputTextSuffix;

      if (highlightedInputTextSuffix.toLowerCase().startsWith(inputTextValue.toLowerCase())) {
        this.inputRef.current.setSelectionRange(
          inputTextValue.length,
          this.inputRef.current.value.length,
          'forward'
        );
      } else {
        this.inputRef.current.setSelectionRange(0, this.inputRef.current.value.length, 'forward');
      }
    }
  }

  onChange(e) {
    const value = e.target.value;

    this.setState(
      () => ({ inputTextValue: value }),
      () => {
        this.props.onTextInputChange(this.state.inputTextValue);
      }
    );
  }

  onClearButtonClick(e) {
    this.setState({
      inputTextValue: ''
    });

    this.props.onClearButtonClick(e);
  }

  onKeyDown(e) {
    if (e.keyCode === keycode.codes.esc) {
      e.preventDefault();

      this.setState({
        inputTextValue: ''
      });

      this.props.onEscKeyClick(e);
    }
  }

  render() {
    const { props } = this;

    const wrapperClasses = ['dc-location-search-input-wrapper'];
    const inputClasses = ['dc-location-search-input', 'nostyle'];
    const labelClasses = ['dc-form-item-label'];

    if (props.searchInputState === 'valid') {
      inputClasses.push('dc-location-search-input-valid');
    }
    if (props.searchInputState === 'invalid') {
      inputClasses.push('dc-location-search-input-invalid');
    }
    if (props.searchInputState === 'neutral') {
      inputClasses.push('dc-location-search-input-neutral');
    }

    if (props.loading || props.disabled) {
      inputClasses.push('dc-search-input-disabled');
    }

    if (this.state.inputTextValue !== '' || this.props.highlightedInputTextSuffix !== '') {
      labelClasses.push('dc-form-item-label-top');
    }
    return (
      <div>
        <InputWrapper
          data-component={'SearchInput'}
          className={wrapperClasses.join(' ')}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Input
            aria-invalid={props.searchInputState === 'valid'}
            aria-label={props.placeholderLabel}
            autoComplete={'off'}
            autoCorrect={'off'}
            className={inputClasses.join(' ')}
            data-element={'Input'}
            disabled={props.disabled}
            searchInputState={props.searchInputState}
            searchInputDisabled={props.loading || props.disabled}
            id={props.name}
            name={props.name}
            onBlur={(e) => {
              e.preventDefault();

              // blocks triggering blur event when user clicks on search button
              if (
                this.searchButtonRef.current === null ||
                this.searchButtonRef.current !== e.relatedTarget
              ) {
                props.onTextInputBlur();
              }
            }}
            onChange={this.onChange}
            onFocus={props.onTextInputFocus}
            onKeyDown={this.onKeyDown}
            ref={this.inputRef}
            spellCheck={'false'}
            tabIndex={props.tabIndex}
            type={'search'}
            value={props.loading ? props.loadingLabel : this.state.inputTextValue}
          />

          {!props.loading && !props.disabled && this.state.inputTextValue !== '' && (
            <ClearSpan>
              <Close onClose={this.onClearButtonClick} />
            </ClearSpan>
          )}

          {!props.loading && !props.disabled && props.searchInputState === 'valid' && (
            <SearchEnabledSpan
              onClick={(e) => {
                props.onSearchButtonClick(this.state.inputTextValue);
              }}
              ref={this.searchButtonRef}
              role={'button'}
              tabIndex={'-1'}
            >
              <Icon icon={'Search'} />
            </SearchEnabledSpan>
          )}

          {!props.loading && !props.disabled && props.searchInputState !== 'valid' && (
            <StyledSpan>
              <Icon icon={'Search'} />
            </StyledSpan>
          )}

          {props.loading && (
            <ProcessingSpan>
              <Icon icon={'Spinner'} spin />
            </ProcessingSpan>
          )}

          <Label
            htmlFor={props.name}
            isOnTop={
              this.state.inputTextValue !== '' || this.props.highlightedInputTextSuffix !== ''
            }
          >
            {' '}
            {/* label Enter town postcode */}
            {props.placeholderLabel}
          </Label>
        </InputWrapper>
      </div>
    );
  }
}

LocationSearchInput.propTypes = {
  name: PropTypes.string.isRequired,
  placeholderLabel: PropTypes.string.isRequired,
  loadingLabel: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  searchInputState: PropTypes.oneOf(['valid', 'invalid', 'neutral']).isRequired,
  highlightedInputTextSuffix: PropTypes.string,
  onTextInputChange: PropTypes.func,
  onClearButtonClick: PropTypes.func,
  onEscKeyClick: PropTypes.func,
  onSearchButtonClick: PropTypes.func,
  onTextInputBlur: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  tabIndex: PropTypes.any,
  onTextInputFocus: PropTypes.func
};

LocationSearchInput.defaultProps = {
  highlightedInputTextSuffix: '',
  onTextInputChange: () => {},
  onClearButtonClick: () => {},
  onEscKeyClick: () => {},
  onSearchButtonClick: () => {},
  onTextInputBlur: () => {},
  onTextInputFocus: () => {},
  tabIndex: null
};
